// Base AWS error
export class AWSError extends Error {
  constructor(
    message: string,
    public originalError?: unknown,
  ) {
    super(message);
    this.name = "AWSError";
  }
}

// Network error (for example, a DNS lookup failure)
export class NetworkError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "NetworkError";
  }
}

// Browser-level fetch rejection from the typed API client: the request never
// produced an HTTP response — a transient network drop, a CORS/preflight
// failure, or an in-flight request cancelled by navigation/unmount. Distinct
// from an HTTP error *status* (those resolve and are handled in the client's
// onResponse middleware), so it's usually transient and non-actionable and is
// reported below error level. Normalized centrally in apiClient's onError hook.
export class ApiNetworkError extends NetworkError {
  constructor(
    public endpoint: string,
    public originalError?: unknown,
    public aborted = false,
  ) {
    super(
      `API request to ${endpoint} failed at the network layer${
        aborted ? " (request aborted)" : ""
      }`,
    );
    this.name = "ApiNetworkError";
  }
}

// An in-flight AWS SDK request the browser cancelled — the user navigated away,
// reloaded, or closed the tab while an invoke was still pending. aws-sdk v2
// surfaces this as `RequestAbortedError` from the XHR `abort` event, which is
// distinct from an explicit `request.abort()` ("Request aborted by user") and
// from a socket timeout (`TimeoutError`). Nothing failed server-side and there
// is nothing for the user to act on, so this is reported below error level and
// shows no notification — the AWS-client counterpart to
// ApiNetworkError(aborted) on the typed API client.
export class AWSRequestAbortedError extends AWSError {
  constructor(
    public functionName: string,
    originalError?: unknown,
  ) {
    super(`AWS request cancelled (${functionName})`, originalError);
    this.name = "AWSRequestAbortedError";
  }
}

// Auth error (for token expiration)
export class AWSAuthError extends AWSError {
  constructor(originalError?: unknown) {
    super("Session has expired", originalError);
    this.name = "AWSAuthError";
  }
}

// Thrown when the local clock is too far out of sync with AWS server time.
export class ClockSkewError extends AWSError {
  constructor(originalError?: unknown) {
    super("Computer clock is out of sync", originalError);
    this.name = "ClockSkewError";
  }
}

// Lambda error.
// `functionName` is kept as a field (not just interpolated into the message)
// so the user-facing error layer can name the failed operation without having
// to parse it back out of the message. See error-handling/userFacingError.ts.
export class LambdaError extends AWSError {
  constructor(
    public functionName: string,
    message = "",
    originalError?: unknown,
    public extra?: Record<string, unknown>,
  ) {
    super(
      `Lambda invocation failed (${functionName}): ${message}`,
      originalError,
    );
    this.name = "LambdaError";
  }
}

// S3 error
export class S3Error extends AWSError {
  constructor(
    public operation: string,
    public bucket: string,
    public key: string,
    error?: unknown,
  ) {
    super(`S3 ${operation} failed (${bucket}/${key})`, error);
    this.name = "S3Error";
  }
}

export class UserError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "UserError";
  }
}

export class UnauthorizedAccessError extends Error {
  constructor(
    public resourceType: string,
    public resourceId: string,
  ) {
    super(
      `Access denied: not authorized to view ${resourceType} (${resourceId})`,
    );
    this.name = "UnauthorizedAccessError";
  }
}

export class InternalError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "InternalError";
  }
}
